import React, { useState, useEffect, useRef } from 'react';
import { X, Play, Save, Loader2 } from 'lucide-react';
import { Button } from './ui/Button';
import { adCreativeMappingApi } from '../lib/api';

interface ConfigRow {
  id: number | string;
  key: string;
  value: any;
}

interface AdminConfigDrawerProps {
  open: boolean;
  onClose: () => void;
  configState: ConfigRow[];
  onConfigSaved?: () => void;
  onPipelineComplete?: () => void;
}

const DEFAULT_PASSES = [
  { key: 'ucode_enabled',  label: 'UCODE',  defaultOn: true },
  { key: 'regex_enabled',  label: 'Regex',  defaultOn: true },
  { key: 'token_enabled',  label: 'Token',  defaultOn: true },
  { key: 'fuzzy_enabled',  label: 'Fuzzy',  defaultOn: false },
];

function getVal(configState: ConfigRow[], key: string, fallback: any) {
  const row = configState.find(r => r.key === key);
  return row !== undefined ? row.value : fallback;
}

export function AdminConfigDrawer({ open, onClose, configState, onConfigSaved, onPipelineComplete }: AdminConfigDrawerProps) {
  const [passes, setPasses] = useState<Record<string, boolean>>({});
  const [regexPattern, setRegexPattern] = useState('');
  const [thresholds, setThresholds] = useState<Record<string, string>>({
    ucode: '1.0',
    regex: '0.8',
    token: '0.7',
    fuzzy: '0.6',
  });
  const [reportEmails, setReportEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [runMessage, setRunMessage] = useState('');
  const [pipelineStatus, setPipelineStatus] = useState<any>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Default pipeline date range: Apr 24 (data start) → yesterday
  const [pipelineDateFrom, setPipelineDateFrom] = useState('2026-04-24');
  const [pipelineDateTo, setPipelineDateTo] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().split('T')[0];
  });

  // Sync config state into local form state whenever it changes
  useEffect(() => {
    const p: Record<string, boolean> = {};
    DEFAULT_PASSES.forEach(({ key, defaultOn }) => {
      p[key] = getVal(configState, key, defaultOn);
    });
    setPasses(p);
    setRegexPattern(getVal(configState, 'regex_pattern', ''));
    const t = { ...thresholds };
    ['ucode', 'regex', 'token', 'fuzzy'].forEach(m => {
      const v = getVal(configState, `${m}_threshold`, null);
      if (v !== null) t[m] = String(v);
    });
    setThresholds(t);
    const emails = getVal(configState, 'report_emails', []);
    setReportEmails(Array.isArray(emails) ? emails : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configState]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const confidenceThresholds: Record<string, number> = {};
      ['ucode', 'regex', 'code', 'token', 'fuzzy'].forEach(m => {
        confidenceThresholds[m] = m === 'code' ? 0.7 : (parseFloat(thresholds[m]) || 0);
      });
      const configs = [
        ...Object.entries(passes).map(([key, value]) => ({ key, value })),
        { key: 'regex_pattern', value: regexPattern },
        { key: 'report_emails', value: reportEmails },
        { key: 'confidence_thresholds', value: confidenceThresholds },
      ];
      await adCreativeMappingApi.saveConfigBulk(configs);
      onConfigSaved?.();
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await adCreativeMappingApi.getPipelineStatus();
        const status = (res?.data as any)?.data;
        setPipelineStatus(status);
        if (!status?.running) {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          setRunning(false);
          if (status?.result) {
            const label = status.error?.includes('Cancelled') ? 'Cancelled (partial saved)' : 'Done';
            setRunMessage(`${label} — matched: ${status.result.matched}, unmatched: ${status.result.unmatched}`);
            if (!status.error?.includes('Cancelled')) onPipelineComplete?.();
          } else if (status?.error) {
            setRunMessage(`${status.error}`);
          }
        }
      } catch { /* ignore poll errors */ }
    }, 3000);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // On open: sync with server so Cancel button shows if pipeline is already running
  useEffect(() => {
    if (!open) return;
    adCreativeMappingApi.getPipelineStatus().then(res => {
      const status = (res?.data as any)?.data;
      if (status?.running) {
        setPipelineStatus(status);
        setRunning(true);
        setRunMessage('Pipeline started…');
        startPolling();
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCancelPipeline = async () => {
    try {
      await adCreativeMappingApi.cancelPipeline();
      setRunMessage('Cancel signal sent — stopping after current pass…');
    } catch (err: any) {
      setRunMessage(err?.response?.data?.error || 'Failed to cancel');
    }
  };

  const handleRunPipeline = async () => {
    setRunning(true);
    setRunMessage('');
    setPipelineStatus(null);
    try {
      await adCreativeMappingApi.runPipeline(pipelineDateFrom, pipelineDateTo);
      setRunMessage('Pipeline started…');
      startPolling();
    } catch (err: any) {
      setRunMessage(err?.response?.data?.error || 'Failed to start pipeline');
      setRunning(false);
    }
  };

  const addEmail = () => {
    const trimmed = newEmail.trim();
    if (trimmed && !reportEmails.includes(trimmed)) {
      setReportEmails([...reportEmails, trimmed]);
    }
    setNewEmail('');
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
      />

      {/* Slide-in panel */}
      <div className="fixed top-0 right-0 h-full w-96 bg-card border-l border-border shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">Analytics Config</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">

          {/* Match Passes */}
          <section>
            <h3 className="text-sm font-semibold text-foreground mb-3">Match Passes</h3>
            <div className="space-y-2">
              {DEFAULT_PASSES.map(({ key, label }) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!passes[key]}
                    onChange={e => setPasses(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="h-4 w-4 rounded border-border text-primary"
                  />
                  <span className="text-sm text-foreground">{label}</span>
                </label>
              ))}
            </div>
          </section>

          {/* Regex Pattern */}
          <section>
            <h3 className="text-sm font-semibold text-foreground mb-2">Regex Pattern</h3>
            <input
              type="text"
              value={regexPattern}
              onChange={e => setRegexPattern(e.target.value)}
              placeholder="e.g. [A-Z]{2}\d{6}"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </section>

          {/* Confidence Thresholds */}
          <section>
            <h3 className="text-sm font-semibold text-foreground mb-3">Confidence Thresholds</h3>
            <div className="space-y-2">
              {(['ucode', 'regex', 'token', 'fuzzy'] as const).map(m => (
                <div key={m} className="flex items-center gap-3">
                  <span className="w-14 text-sm text-muted-foreground capitalize">{m}</span>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                    value={thresholds[m]}
                    onChange={e => setThresholds(prev => ({ ...prev, [m]: e.target.value }))}
                    className="w-24 rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Report Email List */}
          <section>
            <h3 className="text-sm font-semibold text-foreground mb-2">Report Email List</h3>
            <div className="space-y-1 mb-2">
              {reportEmails.map(email => (
                <div key={email} className="flex items-center justify-between bg-muted rounded-md px-3 py-1.5">
                  <span className="text-sm text-foreground truncate">{email}</span>
                  <button
                    onClick={() => setReportEmails(prev => prev.filter(e => e !== email))}
                    className="text-muted-foreground hover:text-destructive ml-2"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addEmail()}
                placeholder="Add email..."
                className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <Button size="sm" variant="outline" onClick={addEmail}>Add</Button>
            </div>
          </section>

          {/* Pipeline Date Range */}
          <section>
            <h3 className="text-sm font-semibold text-foreground mb-2">Pipeline Date Range</h3>
            <div className="flex gap-2">
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs text-muted-foreground">From</label>
                <input type="date" value={pipelineDateFrom} onChange={e => setPipelineDateFrom(e.target.value)}
                  className="rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring w-full" />
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs text-muted-foreground">To</label>
                <input type="date" value={pipelineDateTo} onChange={e => setPipelineDateTo(e.target.value)}
                  className="rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring w-full" />
              </div>
            </div>
          </section>

          {/* Pipeline status */}
          {pipelineStatus && (
            <div className="rounded-md bg-muted px-3 py-2 text-xs space-y-1.5">
              {pipelineStatus.running && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Loader2 size={12} className="animate-spin" />
                  Running since {new Date(pipelineStatus.startedAt).toLocaleTimeString()}…
                  {pipelineStatus.dateFrom && (
                    <span className="ml-1 opacity-70">({pipelineStatus.dateFrom} → {pipelineStatus.dateTo || 'today'})</span>
                  )}
                </div>
              )}
              {/* Per-pass progress — shown while running or after failure */}
              {(() => {
                const prog = pipelineStatus.progress || (pipelineStatus.result?.byMethod ? pipelineStatus.result.byMethod : null);
                const passes = ['ucode', 'regex', 'code', 'token', 'fuzzy'] as const;
                const current = pipelineStatus.currentPass;
                if (!prog && !current) return null;
                return (
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono">
                    {passes.map(p => {
                      const count = prog?.[p];
                      const isActive = pipelineStatus.running && current === p;
                      const isDone = count != null;
                      const isPending = pipelineStatus.running && !isDone && !isActive;
                      return (
                        <span key={p} className={
                          isActive ? 'text-blue-500 dark:text-blue-400' :
                          isDone ? 'text-green-600 dark:text-green-400' :
                          'text-muted-foreground opacity-40'
                        }>
                          {p.toUpperCase()}{isDone ? `: ${count.toLocaleString()}` : isActive ? '…' : ''}
                        </span>
                      );
                    })}
                  </div>
                );
              })()}
              {/* Saved count during run */}
              {pipelineStatus.running && pipelineStatus.savedCount != null && pipelineStatus.savedCount > 0 && (
                <div className="text-muted-foreground">
                  {pipelineStatus.savedCount.toLocaleString()} rows saved to DB so far
                </div>
              )}
              {!pipelineStatus.running && pipelineStatus.result && (
                <div className="text-green-600 dark:text-green-400">
                  Done — matched: {pipelineStatus.result.matched.toLocaleString()} · unmatched: {pipelineStatus.result.unmatched.toLocaleString()}
                  {pipelineStatus.dateFrom && (
                    <span className="ml-1 opacity-70">({pipelineStatus.dateFrom} → {pipelineStatus.dateTo || 'today'})</span>
                  )}
                </div>
              )}
              {!pipelineStatus.running && pipelineStatus.error && (
                <div className="space-y-0.5">
                  <div className="text-destructive">{pipelineStatus.error}</div>
                  {pipelineStatus.savedCount != null && pipelineStatus.savedCount > 0 && (
                    <div className="text-muted-foreground">
                      {pipelineStatus.savedCount.toLocaleString()} rows were saved before the error.
                      Restart with the same date range to pick up remaining passes.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {saveError && (
            <p className="text-sm text-destructive">{saveError}</p>
          )}
          {runMessage && (
            <p className="text-sm text-muted-foreground">{runMessage}</p>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-border px-5 py-4 flex gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRunPipeline}
            disabled={running}
            className="flex items-center gap-1.5"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            {running ? 'Running…' : 'Run Pipeline Now'}
          </Button>
          {running && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelPipeline}
              className="flex items-center gap-1.5 text-destructive border-destructive/50 hover:bg-destructive/10"
            >
              <X size={14} />
              Cancel
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 ml-auto"
          >
            <Save size={14} />
            {saving ? 'Saving...' : 'Save Config'}
          </Button>
        </div>
      </div>
    </>
  );
}
